import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import { Button, Icon, Title } from '../../components';
import { LogoInLine } from '../../components/LogoInLineDark';
import { Bess, BessAddress } from '../../scripts/api/types';
import { useMedia } from '../../scripts/hooks/mediaQueryHook';
import { CountableList } from '../../scripts/types';
import { SBess } from '../../specific/SBess';

const BodyStyle = createGlobalStyle`
  body {
    background-color: ${(props) => props.theme.pallete.primary.main};
  }
`;

const GlobalContainer = styled.div`
  display: grid;
  grid-template:
    'bar' min-content
    '.' 30px
    'besses' 1fr /
    1fr;

  width: 100%;
  height: 100vh;

  justify-content: center;
  align-items: center;

  @media (max-width: 480px) {
    grid-template:
      'bar' min-content
      '.' 30px
      'besses' 1fr
      '.' 30px
      'back' 50px /
      1fr;
  }
`;

const GlobalContainerClient = styled.div`
  display: grid;
  grid-template:
    'logo' min-content
    '.' 30px
    'besses' 1fr
    '.' 30px
    'back' max-content /
    1fr;

  width: 100%;
  height: 80vh;

  justify-content: center;
  align-items: center;

  @media (max-width: 480px) {
    grid-template:
      'bar' min-content
      '.' 30px
      'besses' 1fr
      '.' 30px
      'back' 50px /
      1fr;
  }
`;

const BessesContainer = styled.div<{ $itemCount: number }>`
  display: grid;
  grid-area: besses;
  margin-bottom: 80px;
  gap: 30px;
  justify-content: center;
  align-items: center;

  /* Grid dinâmico baseado no número de itens */
  grid-template-columns: repeat(
    ${(props) => {
      const count = props.$itemCount;
      if (count <= 3) return count;
      if (count <= 8) return 4;
      return 5;
    }},
    200px
  );
  grid-template-rows: repeat(
    ${(props) => {
      const count = props.$itemCount;
      const cols = count <= 3 ? count : count <= 8 ? 4 : 5;
      return Math.ceil(count / cols);
    }},
    max-content
  );

  @media (max-width: 1180px) {
    margin-bottom: 0px;
    grid-template-columns: repeat(
      ${(props) => {
        const count = props.$itemCount;
        if (count <= 2) return count;
        if (count <= 6) return 3;
        return 4;
      }},
      max-content
    );
    grid-template-rows: repeat(
      ${(props) => {
        const count = props.$itemCount;
        const cols = count <= 2 ? count : count <= 6 ? 3 : 4;
        return Math.ceil(count / cols);
      }},
      max-content
    );
  }

  @media (max-width: 950px) {
    grid-template-columns: repeat(
      ${(props) => {
        const count = props.$itemCount;
        if (count <= 2) return count;
        return 3;
      }},
      max-content
    );
    grid-template-rows: repeat(
      ${(props) => {
        const count = props.$itemCount;
        const cols = count <= 2 ? count : 3;
        return Math.ceil(count / cols);
      }},
      max-content
    );
  }

  @media (max-width: 720px) {
    grid-template-columns: repeat(
      ${(props) => {
        const count = props.$itemCount;
        return count === 1 ? 1 : 2;
      }},
      max-content
    );
    grid-template-rows: repeat(
      ${(props) => {
        const count = props.$itemCount;
        const cols = count === 1 ? 1 : 2;
        return Math.ceil(count / cols);
      }},
      max-content
    );
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(${(props) => props.$itemCount}, max-content);
  }
`;

const StyleContainerBar = styled.div`
  display: grid;
  grid-area: bar;
  align-items: center;
  width: 95vw;

  padding: 30px 30px;
  gap: 30px;
  justify-content: space-between;

  grid-template:
    'logo SignOut' 50px /
    max-content max-content;

  @media (max-width: 480px) {
    justify-content: center;
    grid-template:
      'logo' 50px /
      max-content;
  }
`;

export type ViewBessSelectProps = {
  data: CountableList<Bess>;
  /**
   * Called once the user hits the button to select bess.
   */
  onSelect: (id: string) => void;
  /**
   * Called if the user hits the 'Back' button
   */
  onGoBack: () => void;
  /**
   * Function used to filter which besses the user can see
   */
  filterBess: (bess: Bess) => boolean;

  token: string;

  role?: number;
};

/**
 * Bess Select Complete View
 */
export const ViewBessSelect: React.FC<ViewBessSelectProps> = ({
  data,
  onSelect,
  onGoBack,
  filterBess,
  token,
  role,
}: ViewBessSelectProps) => {
  const mobile = useMedia('(max-width:480px)');
  const filteredBesses = data.items.filter(filterBess);
  const bessCount = filteredBesses.length;

  return (
    <>
      <BodyStyle />
      {role !== 256 ? (
        <GlobalContainer>
          <StyleContainerBar>
            <LogoInLine pad='none' width={mobile ? '200px' : '150px'} height={mobile ? '150px' : '100px'} area='logo' />
            {!mobile && <Icon name='SignOut' area='SignOut' onClick={onGoBack} color='white' size='mlarge' />}
          </StyleContainerBar>

          <BessesContainer $itemCount={bessCount}>
            {filteredBesses.map((v, i) => {
              let address: BessAddress | undefined;
              if (v.addresses.length > 0) address = v.addresses[0];
              const city = address?.locality;
              const state = address?.administrativeArea;

              return (
                <SBess
                  key={i}
                  token={token}
                  bess={v._id}
                  title={v.name}
                  subtitle={address ? `${city} - ${state}` : undefined}
                  onClick={() => onSelect(v._id)}
                />
              );
            })}
          </BessesContainer>
          {mobile && (
            <Button
              area='back'
              marginAll={{ bottom: 'large' }}
              radius='small'
              size='small'
              color='light'
              salign={{ horizontal: 'center', vertical: 'center' }}
              onClick={() => onGoBack()}>
              <Title size='small' color='white' value='Voltar' bold />
            </Button>
          )}
        </GlobalContainer>
      ) : (
        <GlobalContainerClient>
          <LogoInLine pad='none' width={mobile ? '200px' : '150px'} height={mobile ? '150px' : '100px'} area='logo' />
          <BessesContainer $itemCount={bessCount}>
            {filteredBesses.map((v, i) => {
              let address: BessAddress | undefined;
              if (v.addresses.length > 0) address = v.addresses[0];
              const city = address?.locality;
              const state = address?.administrativeArea;
              return (
                <SBess
                  key={i}
                  token={token}
                  bess={v._id}
                  title={v.name}
                  subtitle={address ? `${city} - ${state}` : undefined}
                  onClick={() => onSelect(v._id)}
                />
              );
            })}
          </BessesContainer>
          <Button
            area='back'
            marginAll={{ bottom: 'large' }}
            radius='small'
            size='small'
            color='light'
            salign={{ horizontal: 'center', vertical: 'center' }}
            onClick={() => onGoBack()}>
            <Title size='small' color='white' value='Voltar' bold />
          </Button>
        </GlobalContainerClient>
      )}
    </>
  );
};
